import React, { useEffect, useState } from "react";
import localStyles from "./index.module.css";
import commonStyles from '../common/commonStyles/table.module.css';
import { MassiveEvent } from "../dashboard";
import {
    Button,
    Dropdown,
    DropdownProps,
    Form,
    Header,
    Icon,
    Loader,
    Menu,
    Message,
    Modal,
    Table,
} from "semantic-ui-react";
import agent, { PaginateOptions } from "../../agent";
import { Link } from "react-router-dom";
import {
    DoctorTitleCase,
    GeneralTitleCase,
    getDoctorTitle,
    getGeneralTitle,
    getPatientTitle,
    PatientTitleCase,
    getTZ,
} from "../../utils/common";
import { hasRole, UserRole } from "../../helpers/UserRole";
import TablePage from "../general/TablePage";
import { getEventTypesOptions } from "../../helpers/CalendarEventsRepeater";
import { getRusMassiveEventStatus } from "../../consts/EventConsts";
import SafeHTMLRenderer from "../general/SafeHTMLRenderer";
import { stripHtmlTags } from "../general/SlateEditableText/SlateHelpers";
import moment from "moment-timezone";
import { useUrlPagination } from "../../hooks/useUrlPagination";
import {
    CommonListPagePaginationResponse,
    DEFAULT_LIMIT,
    DEFAULT_PAGE,
    handleSortPagination,
    ListState,
} from "../../helpers/TablePageUtils";
import { textLabels } from "../../consts/textLabels";

interface MassiveEventPaginationResponse extends CommonListPagePaginationResponse<MassiveEvent> {}

interface DropdownOptions {
    key: string;
    text: string;
    value: string;
}

export class MassiveEventTypes {
    static ALL = "all";
    static SELECTED = "selected";
    static SPECIAL_GROUPS = "specialgroups";
    static DOCTORS = "doctors";
    static PARAMETERS = "parameters";
}

export class MassiveEventStatus {
    static DONE = "DONE";
    static PENDING = "PENDING";
    static WORK = "WORK";
    static CANCELED = "CANCELED";
}

interface ExtendedListState extends ListState<MassiveEvent> {
    filterEventType?: string;
    filterMassiveEventType?: string;
    filterMassiveEventStatus?: string;
}

const initialState: ExtendedListState = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    sortColumn: "-createDate",
    searchValue: "",
    filterEventType: undefined,
    filterMassiveEventType: undefined,
    filterMassiveEventStatus: undefined,
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    loading: false,
    error: null,
};

const MassiveEventList: React.FC = () => {
    const currentPath = "/dashboard/massive-events";

    const { state, setState, updateUrl } = useUrlPagination<ExtendedListState>(initialState, {
        defaultSortColumn: "-createDate",
        customParams: ["filterEventType", "filterMassiveEventType", "filterMassiveEventStatus"],
    });

    const [searchInput, setSearchInput] = useState(state.searchValue);
    const [openRemoveModal, setOpenRemoveModal] = useState(false);
    const [idToRemove, setIdToRemove] = useState<string | null>(null);

    const fetchList = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const paginateOptions: PaginateOptions = {
            page: state.page,
            limit: state.limit,
            sort: state.sortColumn,
            search: state.searchValue,
            filterEventType: state.filterEventType,
            filterMassiveEventType: state.filterMassiveEventType,
            filterMassiveEventStatus: state.filterMassiveEventStatus,
        };

        try {
            const response: MassiveEventPaginationResponse = await agent.PatientMassiveEventProvider.getList(paginateOptions);

            if (response) {
                setState(prev => ({
                    ...prev,
                    docs: response.docs,
                    totalDocs: response.totalDocs,
                    totalPages: response.totalPages,
                    loading: false,
                    error: null,
                }));
            }
        } catch (error) {
            console.error("Ошибка получения:", error);
            setState(prev => ({
                ...prev,
                docs: [],
                totalDocs: 0,
                totalPages: 1,
                loading: false,
                error: "Не удалось загрузить список массовых рассылок. Пожалуйста, попробуйте позже.",
            }));
        }
    };

    useEffect(() => {
        fetchList();
    }, [
        state.page,
        state.limit,
        state.sortColumn,
        state.searchValue,
        state.filterEventType,
        state.filterMassiveEventType,
        state.filterMassiveEventStatus,
    ]);

    useEffect(() => {
        updateUrl();
    }, [
        state.page,
        state.limit,
        state.sortColumn,
        state.searchValue,
        state.filterEventType,
        state.filterMassiveEventType,
        state.filterMassiveEventStatus,
    ]);

    const handleSearch = () => {
        setState(prev => ({ ...prev, searchValue: searchInput, page: DEFAULT_PAGE }));
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setState(prev => ({
            ...prev,
            searchValue: "",
            filterEventType: undefined,
            filterMassiveEventType: undefined,
            filterMassiveEventStatus: undefined,
            page: DEFAULT_PAGE,
        }));
    };

    const handleSort = (column: string) => {
        const newSort = handleSortPagination(state.sortColumn, column);
        setState(prev => ({ ...prev, sortColumn: newSort, page: DEFAULT_PAGE }));
    };

    const handlePageChange = (page: number) => {
        setState(prev => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setState(prev => ({ ...prev, limit, page: DEFAULT_PAGE }));
    };

    const handleSearchInputChange = (value: string) => {
        setSearchInput(value);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const handleFilterChange = (type: "eventType" | "massiveEventType" | "massiveEventStatus") => (
        _event: React.SyntheticEvent<HTMLElement>,
        data: DropdownProps
    ) => {
        const value = data.value ? String(data.value) : undefined;
        const keyMap = {
            eventType: "filterEventType",
            massiveEventType: "filterMassiveEventType",
            massiveEventStatus: "filterMassiveEventStatus",
        };
        const stateKey = keyMap[type];

        setState(prev => ({ ...prev, [stateKey]: value, page: DEFAULT_PAGE }));
    };

    const openRemoveModalHandler = (id: string) => {
        setIdToRemove(id);
        setOpenRemoveModal(true);
    };

    const closeRemoveModal = () => {
        setOpenRemoveModal(false);
        setIdToRemove(null);
    };

    const removeMassiveEvent = async () => {
        if (!idToRemove) return;

        setState(prev => ({ ...prev, loading: true }));

        try {
            await agent.PatientMassiveEventProvider.remove(idToRemove);
            fetchList();
        } catch (error) {
            console.error("Ошибка удаления:", error);
            setState(prev => ({
                ...prev,
                error: "Не удалось удалить массовую рассылку. Пожалуйста, попробуйте позже.",
            }));
        } finally {
            closeRemoveModal();
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const getMassiveEventTypeOptions = (): DropdownOptions[] => [
        { key: MassiveEventTypes.ALL, text: "Все", value: MassiveEventTypes.ALL },
        {
            key: MassiveEventTypes.SELECTED,
            text: `Выбранные ${getPatientTitle(PatientTitleCase.mult)}`,
            value: MassiveEventTypes.SELECTED,
        },
        { key: MassiveEventTypes.PARAMETERS, text: "По параметрам", value: MassiveEventTypes.PARAMETERS },
    ];

    const getMassiveEventStatusOptions = (): DropdownOptions[] => [
        { key: MassiveEventStatus.PENDING, text: "Запланировано", value: MassiveEventStatus.PENDING },
        { key: MassiveEventStatus.WORK, text: "В работе", value: MassiveEventStatus.WORK },
        { key: MassiveEventStatus.DONE, text: "Выполнено", value: MassiveEventStatus.DONE },
        { key: MassiveEventStatus.CANCELED, text: "Выполнено (есть отмена)", value: MassiveEventStatus.CANCELED },
    ];

    const getTypeEventValue = (chunk: string): string => {
        const typeMapping: { [key: string]: string } = {
            all: "Все",
            selected: "Выбранные",
            doctors: getDoctorTitle(DoctorTitleCase.tab),
            parameters: "По параметрам",
            specialgroups: getGeneralTitle(GeneralTitleCase.specialgrouptab),
        };
        return typeMapping[chunk] || chunk;
    };

    const {
        docs,
        totalDocs,
        totalPages,
        loading,
        error,
        page,
        limit,
        sortColumn,
        filterEventType,
        filterMassiveEventType,
        filterMassiveEventStatus,
    } = state;

    const styles = { ...commonStyles, ...localStyles };
    const headerLabel = "Список массовых рассылок";
    const listLoaderLabel = textLabels.listLoaderMessage;
    const errorLabel = textLabels.errorMessage;

    return (
        <div className={styles.table}>
            <Modal open={openRemoveModal} onClose={closeRemoveModal} closeIcon>
                <Modal.Header>Вы уверены, что хотите удалить массовую рассылку?</Modal.Header>
                <Modal.Actions>
                    <Button content={textLabels.deleteButton.toUpperCase()} onClick={removeMassiveEvent} positive />
                    <Button color="grey" onClick={closeRemoveModal}>
                        {textLabels.cancelButton}
                    </Button>
                </Modal.Actions>
            </Modal>

            <div className={styles.header}><Header>{headerLabel}</Header></div>

            {error && (
                <Message negative>
                    <Message.Header>{errorLabel}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {loading ? (
                <Loader active>{listLoaderLabel}</Loader>
            ) : (
                <div className={styles.list}>
                    <Form>
                        <Form.Group>
                            <Form.Field width={3}>
                                <label>&nbsp;</label>
                                <Form.Input
                                    placeholder="Поиск по описанию"
                                    iconPosition="left"
                                    icon={<Icon name="search" />}
                                    value={searchInput}
                                    onChange={(e) => handleSearchInputChange(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />
                            </Form.Field>

                            <Form.Field width={3}>
                                <label>Статус</label>
                                <Dropdown
                                    selection
                                    clearable
                                    options={getMassiveEventStatusOptions()}
                                    value={filterMassiveEventStatus}
                                    onChange={handleFilterChange("massiveEventStatus")}
                                />
                            </Form.Field>

                            <Form.Field width={3}>
                                <label>Тип рассылки</label>
                                <Dropdown
                                    selection
                                    clearable
                                    options={getMassiveEventTypeOptions()}
                                    value={filterMassiveEventType}
                                    onChange={handleFilterChange("massiveEventType")}
                                />
                            </Form.Field>

                            <Form.Field width={3}>
                                <label>Тип задания</label>
                                <Dropdown
                                    selection
                                    clearable
                                    options={getEventTypesOptions()}
                                    value={filterEventType}
                                    onChange={handleFilterChange("eventType")}
                                />
                            </Form.Field>

                            <Form.Button primary content="Поиск" label="&nbsp;" onClick={handleSearch} />
                            <Form.Button basic color="orange" content="Сбросить" label="&nbsp;" onClick={handleClearSearch} />
                        </Form.Group>
                    </Form>

                    <div className={styles.container}>
                        <Table celled striped verticalAlign="middle" color="teal">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "status" || sortColumn === "-status"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("status")}
                                    >
                                        Статус
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Описание массовой рассылки</Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "type" || sortColumn === "-type"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("type")}
                                    >
                                        Тип рассылки
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "eventType" || sortColumn === "-eventType"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("eventType")}
                                    >
                                        Тип задания
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "startDate" || sortColumn === "-startDate"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("startDate")}
                                    >
                                        Дата начала
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "endDate" || sortColumn === "-endDate"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("endDate")}
                                    >
                                        Дата окончания
                                    </Table.HeaderCell>
                                    {hasRole(UserRole.ADMIN) && <Table.HeaderCell />}
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {docs.map((el) => (
                                    <Table.Row style={{ cursor: "pointer" }} key={`massive-event-${el._id}`}>
                                        <Table.Cell
                                            style={{
                                                color:
                                                    el.status === MassiveEventStatus.PENDING
                                                        ? "#2185d0"
                                                        : el.status === MassiveEventStatus.DONE || el.status === MassiveEventStatus.CANCELED
                                                            ? "#21ba45"
                                                            : "orange",
                                            }}
                                        >
                                            {getRusMassiveEventStatus(el.status)}
                                        </Table.Cell>
                                        <Table.Cell title={stripHtmlTags(el.description)} style={{ whiteSpace: "normal" }}>
                                            <SafeHTMLRenderer html={el.description} maxLength={50} />
                                        </Table.Cell>
                                        <Table.Cell>{getTypeEventValue(el.type)}</Table.Cell>
                                        <Table.Cell>{el.eventType}</Table.Cell>
                                        <Table.Cell>
                                            {el.startDate ? moment(el.startDate).tz(getTZ()).format("DD.MM.YY HH:mm:ss") : "-"}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {el.endDate ? moment(el.endDate).tz(getTZ()).format("DD.MM.YY HH:mm:ss") : "-"}
                                        </Table.Cell>
                                        {hasRole(UserRole.ADMIN) && (
                                            <Table.Cell floated="right" style={{ width: "5%" }}>
                                                {moment(el.endDate).tz(getTZ()).isAfter(moment().tz(getTZ())) && (
                                                    <Button
                                                        icon
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openRemoveModalHandler(el._id);
                                                        }}
                                                    >
                                                        <Icon color="red" name="trash alternate outline" />
                                                    </Button>
                                                )}
                                            </Table.Cell>
                                        )}
                                    </Table.Row>
                                ))}
                            </Table.Body>

                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell colSpan={hasRole(UserRole.ADMIN) ? 7 : 6}>
                                        <Menu floated="right" pagination>
                                            <TablePage
                                                currentPage={page}
                                                totalPages={totalPages}
                                                totalDocs={totalDocs}
                                                limit={limit}
                                                onPageChange={handlePageChange}
                                                onLimitChange={handleLimitChange}
                                            />
                                        </Menu>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MassiveEventList;